import Vue from 'vue'
import Vuex from 'vuex'
import fonts from './assets/fonts.json'
import graphClient from './graphClient'

const Handlebars = require('handlebars')
const axios = require('axios')

// Fonts marked type 'n' in fonts.json are native/system fonts, not Google fonts.
// Requesting a googleapis.com stylesheet for them is a no-op request, so skip them.
const nativeFontFamilies = new Set(
  fonts.fonts.filter((font) => font.type === 'n').map((font) => font.text)
)

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    graphUrl: `${process.env.VUE_APP_GRAPHQL_URL}/?`,
    fonts: [],
    fields: [
      {
        key: 'text_config_title',
        label: 'eTag Configuration',
        type: 'header',
        group: 'text_config',
      },
      {
        key: 'title',
        label: 'Title',
        type: 'textfield',
        group: 'text_config',
        value: 'Selfservice'
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: 'textfield',
        group: 'text_config',
        value: 'Selfservice'
      },
      {
        key: 'text_color',
        label: 'Text Color',
        type: 'color_select',
        group: 'text_config'
      },
      {
        key: 'background_color',
        label: 'Background Color',
        type: 'color_select',
        group: 'text_config'
      }
    ],
    styles: {
      padding: '0px !important',
      background: '#2277dd !important',
      color: '#fff !important',
      'border-radius': 'none !important',
      'border': '0 !important',
      'font-size': '16px !important',
      'font-weight': 'bold !important',
      'font-family': 'Roboto, sans-serif !important',
      'box-shadow': undefined,
      'font-style': undefined,
      'position': 'relative !important'
    },
    values: {
      rotate: false,
      background_color: {
        hex: '#2277dd'
      },
      text_color: {
        hex: '#ffffff'
      },
      logo: '',
      title: 'SJÁLFSAFGREIÐSLA',
      unitLabel: 'T1',
      etagURL: 'https://europe-west1-proton-mainframe.cloudfunctions.net/generateQRCode',
      subtitle: 'Notaðu QR kóðann eða legðu símann að spjaldinu, pantaðu og borgaðu. Við færum þér svo matinn.',
    },
    selectedTags: [],
    selectedFont: null,
    etags: [],
    backgroundColor: '#2277ddff',
    fontsAvailable: fonts.fonts,
    colorSwitch: false,
    widthBeforeBlock: undefined,
    tempColor: '#2277ddff',
    loading: true,
    hasQuery: false,
    textIsItalic: false,
    italicsDisabled: false,
    useOnlyNumbers: false,
    defaultUnit: 'T1',
    selectedFontWeights: ['100','200','300','400','500','600','700','800','900'],
    organization: null,
    locations: [],
    templates: [],
    selectedTemplate: null,
    organizationUUID: null
  },
  mutations: {
    setSelectedFont(state, font) {
      state.selectedFont = font
    },
    setFonts(state, fonts) {
      state.fonts = fonts
    },
    setOrganizationUUID(state, uuid) {
      state.organizationUUID = uuid
    },
    updateLocations(state, locations) {
      state.locations = locations
    },
    updateSelectedTags(state, tags) {
      state.selectedTags = tags
    },
    updateLogo(state, base64Logo) {
      if(typeof state.values !== 'undefined' && state.values !== null) {
        state.values.logo = base64Logo
      }
    },
    updateTags(state, tags) {
      state.tags = tags
    },
    changeValues(state, values) {
      state.values = values
    },
    changeLoading(state, newStatus) {
      state.loading = newStatus
    },
    changeHasQuery(state, status) {
      state.hasQuery = status
    },
    updateOrganization(state, organization) {
      if(state.values && state.values.background_color && state.values.background_color.hex) {
        state.values.background_color.hex = organization.primary_color
      }

      state.organization = organization
    },
    organizationUUID(state, organizationUUID) {
      state.organizationUUID = organizationUUID
    },
    updateTemplates(state, templates) {
      if(Array.isArray(templates) && templates.length > 0) {
        state.selectedTemplate = templates[0]
      }
      state.templates = templates
    },
    selectTemplate(state, template) {
      state.selectedTemplate = template
    },
    changeUseOnlyNumbers(state, value) {
      state.useOnlyNumbers = value
    }
  },
  actions: {
    async fetchGoogleFonts({ commit }) {

      try {
        const apiKey = process.env.VUE_APP_GOOGLE_FONT_API_KEY
        const response = await axios.get(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`)
        commit('setFonts', response.data.items)
      } catch (error) {
        console.error('Error fetching Google Fonts:', error)
      }

    },
    changeText({ commit }, text) {
      commit('changeText', text)
    },
    changeLoadingText({ commit }, text) {
      commit('changeLoadingText', text)
    },
    changeShowLoadingText({ commit }, status) {
      commit('changeShowLoadingText', status)
    },
    changeShowLoadingIcon({ commit }, status) {
      commit('changeShowLoadingIcon', status)
    },
    changeBuyNow({ commit }, status) {
      commit('changeBuyNow', status)
    },
    changeFontSize({ commit }, size) {
      if (this.getters.hasQuery) {
        commit('changeFontSize', size)
        commit('changeHasQuery', false)
      } else {
        commit('changeFontSize', size)
        commit('changeButtonWidth', size*12)
        commit('changeButtonHeight', size+25)
      }
    },
    changeFontSizeOnly({ commit }, size) {
      commit('changeFontSize', size)
    },
    changePadding({ commit }, padding) {
      commit('changePadding', padding)
    },
    changeButtonElevation({ commit }, elevation) {
      commit('changeButtonElevation', elevation)
    },
    changeButtonMinWidth({ commit }, width) {
      commit('changeButtonMinWidth', width)
    },
    changeButtonMinHeight({ commit }, height) {
      commit('changeButtonMinHeight', height)
    },
    changeBackgroundColor({ commit }, color) {
      commit('changeBackgroundColor', color)
    },
    changeTextColor({ commit }, color) {
      commit('changeTextColor', color)
    },
    changeButtonType({ commit }, buttonType) {
      commit('changeButtonType', buttonType)
      if(buttonType && (buttonType.includes('Outlined') || buttonType.includes('Text'))) {
        this.state.colorSwitch = true
      } else {
        this.state.colorSwitch = false
      }
    },
    getInitialData({ state, commit, dispatch }, organizationUUID) {
      commit('changeLoading', true)
      dispatch('fetchGoogleFonts')

      graphClient({
        url: state.graphUrl,
        method: 'post',
        data: {
          query: 'query {\n' +
              '  organization(uuid: "' + organizationUUID + '") {\n' +
              '    label\n' +
              '    primary_color\n' +
              '    accent_color\n' +
              '    uuid\n' +
              '  }\n' +
              '  etags(uuid: "' + organizationUUID + '") {\n' +
              '    tagID\n' +
              '    property\n' +
              '    unitID\n' +
              '    type\n' +
              '  }\n' +
              '  locationOptions(organization: "' + organizationUUID + '") {\n' +
              '    uuid\n' +
              '    label\n' +
              '    variations {\n' +
              '      uuid\n' +
              '      label\n' +
              '    }\n' +
              '  }\n' +
              '  etagTemplates(organization: "' + organizationUUID + '") {\n' +
              '    uuid\n' +
              '    name\n' +
              '    isReadOnly\n' +
              '    mjml\n' +
              '    active\n' +
              '    namespace\n' +
              '  }\n' +
              '}'
        }
      }).then(response => {
        if(response && response.data && response.data.data && response.data.data.organization) {
          commit('updateOrganization', response.data.data.organization)
          commit('updateTags', response.data.data.etags)
          commit('updateLocations', response.data.data.locationOptions)
          commit('updateTemplates', response.data.data.etagTemplates)
        }
      })
        .catch(function() {
        }.bind(this))
        .finally(function(){
          commit('changeLoading', false)
        }.bind(this))
    },
    changeChannelSelection({ commit }, channel) {
      commit('changeChannelSelection', channel)
    },
    changeProductSelection({ commit }, product) {
      commit('changeProductSelection', product)
    },
    changeBuyNowSelection({ commit }, status) {
      commit('changeBuyNowSelection', status)
    },
    changeButtonMaxWidth({ commit }, width) {
      commit('changeButtonMaxWidth', width)
    },
    changeButtonMaxHeight({ commit }, height) {
      commit('changeButtonMaxHeight', height)
    },
    changeButtonWidth({ commit }, width) {
      commit('changeButtonWidth', width)
    },
    changeButtonHeight({ commit }, height) {
      commit('changeButtonHeight', height)
    },
    changeTextUpper({ commit }, status) {
      commit('changeTextUpper', status)
      if(status) {
        commit('changeText', this.getters.text.toUpperCase())
        commit('changeLoadingText', this.getters.loadingText.toUpperCase())
      }
    }, // unused
    changeTextLower({ commit }, status) {
      commit('changeTextLower', status)
      commit('changeText', this.getters.text.toLowerCase())
      commit('changeLoadingText', this.getters.loadingText.toLowerCase())
    }, // unused
    changeTextFont({ commit }, font) {
      commit('changeTextFont', font)
    },
    changeLoading({ commit }, newStatus) {
      commit('changeLoading', newStatus)
    },
    changeHasQuery({ commit }, newStatus) {
      commit('changeHasQuery', newStatus)
    },
    changeFontWeight({ commit }, newWeight) {
      commit('changeFontWeight', newWeight)
    },
    changeFontStyle({ commit }, newStyle) {
      commit('changeFontStyle', newStyle)
    },
    updateOrganizationName({ commit }, newName) {
      commit('changeOrganizationName', newName)
    },
    changeTextIsItalic({ commit }, newStatus) {
      commit('changeTextIsItalic', newStatus)
    },
    changeCheckoutLanguage({ commit }, newSelection) {
      commit('changeCheckoutLanguage', newSelection)
    },
    changeItalicsDisabled({ commit }, newStatus) {
      commit('changeItalicsDisabled', newStatus)
    },
    changeSelectedFontWeights({ commit }, newWeights) {
      commit('changeSelectedFontWeights', newWeights)
    }
  },
  getters: {
    tags: (state) => {
      const tags = []

      const locations = {}
      if(Array.isArray(state.locations)) {
        for (let locationIndex = 0; locationIndex < state.locations.length; locationIndex++) {
          const location = state.locations[locationIndex]
          if (location) {
            locations[location.uuid] = location
            const variations = {}
            if (Array.isArray(location.variations)) {
              for (let variationIndex = 0; variationIndex < location.variations.length; variationIndex++) {
                const variation = location.variations[variationIndex]
                if (variation) {
                  variations[variation.uuid] = variation
                }
              }
            }
            locations[location.uuid].variations = variations
          }
        }
      }

      if(Array.isArray(state.tags)) {
        for(let i = 0; i < state.tags.length; i++) {
          const tag = JSON.parse(JSON.stringify(state.tags[i]))

          if(tag.property) {
            tag.properties = tag.property.split(/(?=&)/g)

            tag.location = tag.properties.find(x => x.includes('&l='))

            if(typeof tag.location !== 'undefined' && tag.location !== null) {
              tag.location = tag.location.slice(3)
            }

            tag.unitID = tag.properties.find(x => x.includes('&unitID='))

            if(typeof tag.unitID !== 'undefined' && tag.unitID !== null) {
              tag.unitID = tag.unitID.slice(8)
            }
          }

          tag.readableLabel = 'No location or no unit id set on etag'

          if(tag.location && tag.unitID) {
            if(locations[tag.location]) {
              if(locations[tag.location].variations) {
                if(locations[tag.location].variations[tag.unitID]) {
                  tag.readableLabel = locations[tag.location].variations[tag.unitID].label + ' - ' + locations[tag.location].label
                } else {
                  tag.readableLabel = locations[tag.location].label
                }
              }
            } else {
              console.log('Location set on tag does not exist on organization or is disabled')
              tag.readableLabel = tag.unitID
              tag.errorText = 'Location set on tag is inconsistent with available locations'
            }
          } else {
            console.log('tag has no location or unit id', tag)
          }

          tags.push(tag)
        }
      }
      return tags
    },
    values: (state) => {
      return state.values
    },
    styles: (state) => {

      let style = state.styles

      if(state.values.text_color) {
        style.color = state.values.text_color.hex
      }

      if(state.values.background_color) {
        style.background = state.values.background_color.hex
      }

      if(state.selectedFont !== null && state.selectedFont !== undefined) {
        style['font-family'] = '"' + state.selectedFont.family + '"' + ', sans-serif'
      }

      return style
    },
    styleStringer: (state, getters) => {
      return Object.entries(getters.styles)
        .filter(function(i) { return i[1] !== undefined || i[1] != null })
        .reduce((styleString, [propName, propValue]) => {
          return `${styleString}${propName}:${propValue};`
        }, '')
    },
    etagHtml: (state, getters) => {

      let string = '<html><head>'
      if(state.selectedFont === null || state.selectedFont === undefined) {
        string += '<link href="https://fonts.googleapis.com/css?family=Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i&display=swap" rel="stylesheet">'
      }
      else if(!nativeFontFamilies.has(state.selectedFont.family)) {
        string += '<link href="https://fonts.googleapis.com/css?family=' + state.selectedFont.family + ':100,100i,300,300i,400,400i,500,500i,700,700i,900,900i&display=swap" rel="stylesheet">'
      }

      string +=  '</head><body style="margin: 0; display: table; padding: 0; text-align: center; vertical-align: middle; -webkit-font-smoothing: antialiased;">'

      for(let i in state.selectedTags) {
        const tag = state.selectedTags[i]
        const values = JSON.parse(JSON.stringify(state.values))
        values.rotate = true

        const url = new URL(tag.property)
        const unitID = parseInt(url.searchParams.get('unitID'))

        for(let i in state.locations) {

          if(state.locations[i].variations === undefined || state.locations[i].variations === null || Object.keys(state.locations[i].variations).length === 0 || (Array.isArray(state.locations[i].variations) && state.locations[i].variations.length === 0)) {
            continue
          }
          for(let n in state.locations[i].variations) {
            const variation = state.locations[i].variations[n]
            if(variation.uuid === unitID) {
              values.unitLabel = variation.label
            }
          }
        }

        values.etagURL = 'https://europe-west1-proton-mainframe.cloudfunctions.net/generateQRCode?qrContent=https://etags.salescloud.is/' + tag.tagID
        string += getters.renderHtml(values)
      }

      string += '</body></html>'

      return string
    },
    iframeHtml: (state, getters) => {

      let string = '<html><head>'
      if(state.selectedFont === null || state.selectedFont === undefined) {
        string += '<link href="https://fonts.googleapis.com/css?family=Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i&display=swap" rel="stylesheet">'
      }
      else if(!nativeFontFamilies.has(state.selectedFont.family)) {
        string += '<link href="https://fonts.googleapis.com/css?family=' + state.selectedFont.family + ':100,100i,300,300i,400,400i,500,500i,700,700i,900,900i&display=swap" rel="stylesheet">'
      }
      string +=  '</head><body style="margin: 0; padding: 0; text-align: center; vertical-align: middle; -webkit-font-smoothing: antialiased;">'
      string += getters.renderHtml(state.values)
      string += '</body></html>'

      return string
    },
    renderHtml: (state, getters) => (values) => {

      if(state.selectedTemplate === null || typeof state.selectedTemplate === 'undefined' || state.selectedTemplate.mjml === null || typeof state.selectedTemplate.mjml === 'undefined') {
        return ''
      }

      const templateCode = state.selectedTemplate.mjml

      const template = Handlebars.compile(templateCode)

      let styleString = ''

      if(values.rotate) {
        styleString += 'height: 289pt; width: 209.8pt; margin:4.2pt; float:left; '
      } else {
        styleString += 'float: left; margin: 0 auto; height: 289pt; width: 209.8pt; '
      }

      if(state.selectedTemplate.name.includes('etag_template')) {
        styleString += getters.styleStringer
      }

      if(values.etagURL === null || typeof values.etagURL === 'undefined' || values.etagURL === '') {
        values.etagURL = 'https://europe-west1-proton-mainframe.cloudfunctions.net/generateQRCode'
      }

      if(state.useOnlyNumbers) {
        values.unitLabel = values.unitLabel.replace(/\D/g,'')
      }

      const renderedTemplate = template({
        style: styleString,
        title: values.title,
        subtitle: values.subtitle,
        unitLabel: values.unitLabel,
        etagURL: values.etagURL,
        logo: values.logo,
        textColor: values.text_color.hex
      })

      return renderedTemplate
    }
  }
})
